var _excluded = ["onChange", "onBlur", "onFocus", "value", "placeholder", "isDisabled", "autoFocus", "onClear", "onKeyDown", "onKeyUp", "aria-label", "isFullWidth"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { createRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Group } from '../../../containers';
import { Icon } from '../../../foundations/Icon';
import { forwardRef } from '../../../utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { IconButton } from '../../action';
import { Box } from '../../internal';
import { InputGroup } from '../../internal/InputGroup';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var SearchBar = forwardRef((_ref, forwardRef) => {
  var {
    onChange,
    onBlur,
    onFocus,
    value,
    placeholder,
    isDisabled = false,
    autoFocus,
    onClear,
    onKeyDown: _onKeyDown,
    onKeyUp: _onKeyUp,
    'aria-label': ariaLabel = 'Search',
    isFullWidth = false
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var [localValue, setLocalValue] = useState(value);
  var [isInputFocus, setInputFocus] = useState(false);
  var style = useMultiStyleConfig('SearchBar', {
    hasValue: Boolean(localValue),
    isFullWidth
  });
  var inputRef = /*#__PURE__*/createRef();
  var clearButtonRef = useRef(null);
  useImperativeHandle(forwardRef, () => inputRef.current);
  var handleOnChange = useCallback(e => {
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
    setLocalValue(e.target.value);
  }, [onChange]);
  var handleOnClear = useCallback(() => {
    onClear === null || onClear === void 0 ? void 0 : onClear();
    handleOnChange({
      target: {
        value: ''
      }
    });
    setInputFocus(true);
  }, [handleOnChange, onClear]);
  var handleOnBlur = useCallback(e => {
    if (!e.relatedTarget || e.relatedTarget !== clearButtonRef.current) {
      if (isInputFocus) {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
      }

      setInputFocus(false);
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [onBlur]);
  var handleOnFocus = useCallback(e => {
    if (!isInputFocus) {
      onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }

    setInputFocus(true);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [onFocus]);
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  return /*#__PURE__*/_jsx(InputGroup, _objectSpread(_objectSpread({
    "data-component": "SearchBar",
    __css: style['container'],
    "aria-disabled": isDisabled || undefined,
    "data-focus": !isDisabled && isInputFocus || undefined,
    onClick: () => {
      var _inputRef$current;

      return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    }
  }, props), {}, {
    children: /*#__PURE__*/_jsxs(Group, {
      alignItems: "center",
      spacing: "xs",
      width: "full",
      children: [/*#__PURE__*/_jsx(Icon, {
        type: "search",
        color: "inherit",
        size: "small"
      }), /*#__PURE__*/_jsx(Box, {
        as: "input" // ts-ignore is used due to `type` issues with `<Box/>`.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        ,
        type: "text",
        "aria-label": ariaLabel,
        "data-testid": "search-input",
        placeholder: placeholder,
        value: localValue,
        disabled: isDisabled,
        onChange: handleOnChange,
        onFocus: handleOnFocus,
        onBlur: handleOnBlur,
        onKeyUp: e => _onKeyUp === null || _onKeyUp === void 0 ? void 0 : _onKeyUp(e),
        onKeyDown: e => _onKeyDown === null || _onKeyDown === void 0 ? void 0 : _onKeyDown(e),
        "data-focus": !isDisabled && isInputFocus || undefined,
        sx: style['field'],
        autoFocus: autoFocus,
        ref: inputRef
      }), Boolean(localValue) && /*#__PURE__*/_jsx(IconButton, {
        icon: "close",
        "aria-label": "clear search",
        variant: "naked",
        size: "small",
        "data-testid": "search-clear-button",
        tabIndex: localValue ? 0 : -1,
        onClick: handleOnClear,
        isDisabled: isDisabled,
        ref: clearButtonRef
      })]
    })
  }));
});
SearchBar.displayName = 'SearchBar';
//# sourceMappingURL=SearchBar.js.map